// Asistente de predicciones paso a paso
import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { 
  ChevronLeft, ChevronRight, Save, Check, 
  Users, GitBranch, Award, Medal, Lock
} from 'lucide-react'
import { useQuery } from '@tanstack/react-query'
import client from '../api/client'
import { parseBoliviaDate } from '../api/dateUtils'
import Button from '../components/ui/Button'
import Badge from '../components/ui/Badge'
import GroupPicker from '../components/predictions/GroupPicker'
import ThirdsPicker from '../components/predictions/ThirdsPicker'
import BracketView from '../components/predictions/BracketView'
import AwardsPicker from '../components/predictions/AwardsPicker'
import { useToast } from '../components/ui/Toast'
import Skeleton from '../components/ui/Skeleton'

// Definición de los pasos del asistente
const wizardSteps = [
  { key: 'groups', label: 'Fase de Grupos', icon: Users, description: 'Ordena los equipos de cada grupo' },
  { key: 'thirds', label: 'Mejores Terceros', icon: Medal, description: 'Selecciona los 8 mejores terceros' },
  { key: 'bracket', label: 'Eliminatorias', icon: GitBranch, description: 'Predice cada partido eliminatorio' },
  { key: 'awards', label: 'Premios', icon: Award, description: 'Predice los premios individuales' },
]

// Animaciones de transición entre pasos
const pageVariants = {
  enter: (direction) => ({
    x: direction > 0 ? 300 : -300,
    opacity: 0,
  }),
  center: {
    x: 0,
    opacity: 1,
    transition: { duration: 0.4, ease: [0.22, 1, 0.36, 1] },
  },
  exit: (direction) => ({
    x: direction < 0 ? 300 : -300,
    opacity: 0,
    transition: { duration: 0.3 },
  }),
}

// Función auxiliar para inicializar la fase de dieciseisavos a partir de las predicciones de grupos
const generateInitialBracket = (groupsDraft, thirdsDraft) => {
  const getTeam = (groupLetter, rankIndex) => {
    const groupTeams = groupsDraft?.[groupLetter]
    return groupTeams ? groupTeams[rankIndex] : null
  }

  // Obtener los 8 terceros seleccionados
  const thirdsTeams = (thirdsDraft || []).map(letter => getTeam(letter, 2)).filter(Boolean)
  while (thirdsTeams.length < 8) {
    thirdsTeams.push(null)
  }

  return [
    // 8 partidos: Ganadores A-H vs mejores terceros 1-8
    { teamA: getTeam('A', 0), teamB: thirdsTeams[0], winner: null },
    { teamA: getTeam('B', 0), teamB: thirdsTeams[1], winner: null },
    { teamA: getTeam('C', 0), teamB: thirdsTeams[2], winner: null },
    { teamA: getTeam('D', 0), teamB: thirdsTeams[3], winner: null },
    { teamA: getTeam('E', 0), teamB: thirdsTeams[4], winner: null },
    { teamA: getTeam('F', 0), teamB: thirdsTeams[5], winner: null },
    { teamA: getTeam('G', 0), teamB: thirdsTeams[6], winner: null },
    { teamA: getTeam('H', 0), teamB: thirdsTeams[7], winner: null },

    // 4 partidos: Ganadores I-L vs segundos J, I, L, K
    { teamA: getTeam('I', 0), teamB: getTeam('J', 1), winner: null },
    { teamA: getTeam('J', 0), teamB: getTeam('I', 1), winner: null },
    { teamA: getTeam('K', 0), teamB: getTeam('L', 1), winner: null },
    { teamA: getTeam('L', 0), teamB: getTeam('K', 1), winner: null },

    // 4 partidos: Segundos A-H juegan entre sí
    { teamA: getTeam('A', 1), teamB: getTeam('B', 1), winner: null },
    { teamA: getTeam('C', 1), teamB: getTeam('D', 1), winner: null },
    { teamA: getTeam('E', 1), teamB: getTeam('F', 1), winner: null },
    { teamA: getTeam('G', 1), teamB: getTeam('H', 1), winner: null },
  ]
}

export default function PredictionsWizard() {
  const [currentStep, setCurrentStep] = useState(0)
  const [direction, setDirection] = useState(0)
  const toast = useToast()

  // Estado central de predicciones
  const [predictions, setPredictions] = useState({
    groups: {},
    thirds: [],
    bracket: {},
    awards: {},
  })

  // 1. Obtener la configuración de grupos/equipos reales del backend
  const { data: groupsData, isLoading: isLoadingGroups } = useQuery({
    queryKey: ['groups'],
    queryFn: async () => {
      const response = await client.get('/groups')
      return response.data?.datos || []
    },
    staleTime: 30 * 60 * 1000,
  })
  const groups = groupsData || []

  // Obtener configuraciones públicas (puntos y fecha de cierre)
  const { data: settingsData } = useQuery({
    queryKey: ['settings'],
    queryFn: async () => {
      const response = await client.get('/settings')
      return response.data?.datos
    }
  })

  // 2. Comprobar si el usuario ya tiene predicciones registradas en el backend
  const { data: existingPredictionsData, isLoading: isLoadingExisting } = useQuery({
    queryKey: ['userPredictions'],
    queryFn: async () => {
      const response = await client.get('/predictions')
      return response.data
    },
  })
  const existingPredictions = existingPredictionsData?.datos || []
  const hasExistingPrediction = existingPredictions.length > 0
  const existingPredictionId = existingPredictions[0]?.id

  const fechaCierreStr = settingsData?.fecha_cierre
  const esComenzado = settingsData?.comenzado === true || settingsData?.comenzado === '1'
  const bloqueoDinamico = settingsData?.bloqueo_dinamico_partidos === undefined || settingsData?.bloqueo_dinamico_partidos === '1' || settingsData?.bloqueo_dinamico_partidos === 1
  const limiteSuperado = fechaCierreStr ? new Date() > parseBoliviaDate(fechaCierreStr) : false
  const isLocked = bloqueoDinamico ? (esComenzado || limiteSuperado) : false
  
  // Es de solo lectura si el mundial ya empezó o venció el plazo de cierre
  const isReadOnly = isLocked

  // 3. Si tiene predicción, obtener todos sus detalles
  const { data: fullPredictionData, isLoading: isLoadingFull } = useQuery({
    queryKey: ['fullPrediction', existingPredictionId],
    queryFn: async () => {
      const response = await client.get(`/predictions/${existingPredictionId}`)
      return response.data
    },
    enabled: !!existingPredictionId,
  })

  // Cargar borrador local si no hay predicciones en base de datos
  useEffect(() => {
    if (groups.length > 0 && !hasExistingPrediction) {
      const saved = localStorage.getItem('subigol_predictions_draft')
      if (saved) {
        try {
          setPredictions(JSON.parse(saved))
        } catch (e) {
          // ignorar
        }
      }
    }
  }, [groups, hasExistingPrediction])

  // Guardar borrador automáticamente en localStorage (si no está enviado)
  useEffect(() => {
    if (!hasExistingPrediction && Object.keys(predictions.groups).length > 0) {
      localStorage.setItem('subigol_predictions_draft', JSON.stringify(predictions))
    }
  }, [predictions, hasExistingPrediction])

  // Reconstruir estado a partir de la predicción de la base de datos
  useEffect(() => {
    if (fullPredictionData?.datos && groups.length > 0) {
      const pred = fullPredictionData.datos
      
      // Reconstruir grupos
      const groupsState = {}
      pred.grupos.forEach(g => {
        const groupObj = groups.find(grp => grp.nombre === g.grupo_nombre)
        if (groupObj) {
          const team1 = groupObj.equipos.find(t => Number(t.id) === Number(g.primer_lugar_id))
          const team2 = groupObj.equipos.find(t => Number(t.id) === Number(g.segundo_lugar_id))
          const otherTeams = groupObj.equipos.filter(t => Number(t.id) !== Number(g.primer_lugar_id) && Number(t.id) !== Number(g.segundo_lugar_id))
          groupsState[g.grupo_nombre] = [team1, team2, ...otherTeams].filter(Boolean)
        }
      })

      // Reconstruir mejores terceros
      const thirdsState = []
      pred.mejores_terceros.forEach(t => {
        const groupObj = groups.find(grp => grp.equipos.some(team => Number(team.id) === Number(t.equipo_id)))
        if (groupObj) {
          thirdsState.push(groupObj.nombre)
        }
      })

      // Reconstruir brackets
      const bracketState = {}
      const dieciseisavosWinners = pred.brackets.filter(b => b.fase === 'dieciseisavos').map(b => Number(b.equipo_id))
      const octavosWinners = pred.brackets.filter(b => b.fase === 'octavos').map(b => Number(b.equipo_id))
      const cuartosWinners = pred.brackets.filter(b => b.fase === 'cuartos').map(b => Number(b.equipo_id))
      const semifinalWinners = pred.brackets.filter(b => b.fase === 'semifinal').map(b => Number(b.equipo_id))
      const finalWinners = pred.brackets.filter(b => b.fase === 'final').map(b => Number(b.equipo_id))
      const tercerPuestoWinners = pred.brackets.filter(b => b.fase === 'tercer_puesto').map(b => Number(b.equipo_id))

      const dieciseisavosMatches = generateInitialBracket(groupsState, thirdsState)
      dieciseisavosMatches.forEach(m => {
        if (m.teamA && dieciseisavosWinners.includes(Number(m.teamA.id))) m.winner = m.teamA
        else if (m.teamB && dieciseisavosWinners.includes(Number(m.teamB.id))) m.winner = m.teamB
      })
      bracketState['dieciseisavos'] = dieciseisavosMatches

      const propagateAndSetWinners = (currentPhase, nextPhase, nextPhaseWinners) => {
        const currentMatches = bracketState[currentPhase] || []
        const nextMatches = []
        for (let i = 0; i < currentMatches.length; i += 2) {
          const matchA = currentMatches[i]
          const matchB = currentMatches[i + 1]
          const teamA = matchA?.winner || null
          const teamB = matchB?.winner || null
          let winner = null
          if (teamA && nextPhaseWinners.includes(Number(teamA.id))) winner = teamA
          else if (teamB && nextPhaseWinners.includes(Number(teamB.id))) winner = teamB
          nextMatches.push({ teamA, teamB, winner })
        }
        bracketState[nextPhase] = nextMatches
      }

      propagateAndSetWinners('dieciseisavos', 'octavos', octavosWinners)
      propagateAndSetWinners('octavos', 'cuartos', cuartosWinners)
      propagateAndSetWinners('cuartos', 'semifinal', semifinalWinners)

      const semiMatches = bracketState['semifinal'] || []
      const finalMatch = { teamA: null, teamB: null, winner: null }
      const thirdMatch = { teamA: null, teamB: null, winner: null }

      if (semiMatches[0]) {
        finalMatch.teamA = semiMatches[0].winner
        thirdMatch.teamA = semiMatches[0].winner?.id === semiMatches[0].teamA?.id ? semiMatches[0].teamB : semiMatches[0].teamA
      }
      if (semiMatches[1]) {
        finalMatch.teamB = semiMatches[1].winner
        thirdMatch.teamB = semiMatches[1].winner?.id === semiMatches[1].teamA?.id ? semiMatches[1].teamB : semiMatches[1].teamA
      }

      if (finalMatch.teamA && finalWinners.includes(Number(finalMatch.teamA.id))) finalMatch.winner = finalMatch.teamA
      else if (finalMatch.teamB && finalWinners.includes(Number(finalMatch.teamB.id))) finalMatch.winner = finalMatch.teamB

      if (thirdMatch.teamA && tercerPuestoWinners.includes(Number(thirdMatch.teamA.id))) thirdMatch.winner = thirdMatch.teamA
      else if (thirdMatch.teamB && tercerPuestoWinners.includes(Number(thirdMatch.teamB.id))) thirdMatch.winner = thirdMatch.teamB

      bracketState['final'] = [finalMatch]
      bracketState['tercer_puesto'] = [thirdMatch]

      // Reconstruir premios
      const awardsState = {}
      if (pred.premios) {
        const champTeam = groups.flatMap(g => g.equipos).find(t => Number(t.id) === Number(pred.premios.campeon_equipo_id))
        awardsState.campeon = champTeam || null
        awardsState.goleador = pred.premios.maximo_goleador_id ? { id: Number(pred.premios.maximo_goleador_id), nombre: pred.premios.goleador_nombre } : null
        awardsState.mejorJugador = pred.premios.mejor_jugador_id ? { id: Number(pred.premios.mejor_jugador_id), nombre: pred.premios.mejor_jugador_nombre } : null
      }

      setPredictions({
        groups: groupsState,
        thirds: thirdsState,
        bracket: bracketState,
        awards: awardsState,
      })
    }
  }, [fullPredictionData, groups])

  // Navegar al paso siguiente
  const goNext = () => {
    if (currentStep === 1 && !isReadOnly) {
      // Inicializar bracket al pasar a la fase eliminatoria
      const initialMatches = generateInitialBracket(predictions.groups, predictions.thirds)
      setPredictions(prev => ({
        ...prev,
        bracket: {
          ...prev.bracket,
          dieciseisavos: initialMatches,
        }
      }))
    }
    if (currentStep === 2 && !isReadOnly) {
      // Inicializar campeón del bracket al pasar a premios
      const finalWinner = predictions.bracket.final?.[0]?.winner
      setPredictions(prev => ({
        ...prev,
        awards: {
          ...prev.awards,
          campeon: finalWinner || null,
        }
      }))
    }

    if (currentStep < wizardSteps.length - 1) {
      setDirection(1)
      setCurrentStep(prev => prev + 1)
    }
  }

  // Navegar al paso anterior
  const goPrev = () => {
    if (currentStep > 0) {
      setDirection(-1)
      setCurrentStep(prev => prev - 1)
    }
  }

  // Ir a un paso específico
  const goToStep = (index) => {
    setDirection(index > currentStep ? 1 : -1)
    setCurrentStep(index)
  }

  // Guardar borrador localmente en el servidor
  const handleSave = () => {
    toast.success('Borrador guardado localmente', '¡Borrador Guardado!')
  }

  // Enviar predicciones finales a la API
  const handleSubmit = async () => {
    // Validar fase de grupos completa
    const missingGroupKeys = groups.filter(g => !predictions.groups[g.nombre])
    if (missingGroupKeys.length > 0) {
      toast.warning('Por favor completa el ordenamiento de todos los grupos.')
      return
    }

    // Validar mejores terceros
    if ((predictions.thirds || []).length !== 8) {
      toast.warning('Por favor selecciona exactamente 8 mejores terceros.')
      return
    }

    // Validar campeón en bracket
    const champion = predictions.bracket.final?.[0]?.winner
    if (!champion) {
      toast.warning('Por favor completa el bracket eliminatorio y selecciona un campeón.')
      return
    }

    // Formatear payload para la API
    const gruposPayload = Object.entries(predictions.groups).map(([groupName, teams]) => {
      const groupObj = groups.find(g => g.nombre === groupName)
      return {
        grupo_id: groupObj?.id,
        primer_lugar_id: teams[0]?.id,
        segundo_lugar_id: teams[1]?.id,
      }
    })

    const mejoresTercerosPayload = predictions.thirds.map(letter => {
      const groupTeams = predictions.groups[letter]
      return groupTeams ? groupTeams[2]?.id : null
    }).filter(Boolean)

    const bracketsPayload = []
    const phases = ['dieciseisavos', 'octavos', 'cuartos', 'semifinal', 'tercer_puesto', 'final']
    phases.forEach(phase => {
      const phaseMatches = predictions.bracket[phase] || []
      phaseMatches.forEach(match => {
        if (match.winner) {
          bracketsPayload.push({
            fase: phase,
            equipo_id: match.winner.id,
          })
        }
      })
    })

    const premiosPayload = {
      campeon_equipo_id: champion.id,
      maximo_goleador_id: predictions.awards.maximo_goleador?.id || null,
      mejor_jugador_id: predictions.awards.mejor_jugador?.id || null,
    }

    const payload = {
      grupos: gruposPayload,
      mejores_terceros: mejoresTercerosPayload,
      brackets: bracketsPayload,
      premios: premiosPayload,
    }

    try {
      await client.post('/predictions', payload)
      toast.success(hasExistingPrediction ? 'Tus predicciones han sido actualizadas.' : 'Tus predicciones han sido enviadas. ¡Buena suerte!', '¡Predicción guardada!')
      localStorage.removeItem('subigol_predictions_draft')
      window.location.href = (window.__APP_BASE_URL__ || '/') + 'dashboard'
    } catch (err) {
      toast.error(err.response?.data?.mensaje || 'Error al guardar las predicciones.')
    }
  }

  // Renderizar el contenido del paso actual
  const renderStepContent = () => {
    switch (wizardSteps[currentStep].key) {
      case 'groups':
        return (
          <GroupPicker
            predictions={predictions.groups}
            onUpdate={(groups) => !isReadOnly && setPredictions(prev => ({ ...prev, groups }))}
            groups={groups}
          />
        )
      case 'thirds':
        return (
          <ThirdsPicker
            groupPredictions={predictions.groups}
            selectedThirds={predictions.thirds}
            onUpdate={(thirds) => !isReadOnly && setPredictions(prev => ({ ...prev, thirds }))}
          />
        )
      case 'bracket':
        return (
          <BracketView
            predictions={predictions.bracket}
            onUpdate={(bracket) => !isReadOnly && setPredictions(prev => ({ ...prev, bracket }))}
          />
        )
      case 'awards':
        return (
          <AwardsPicker
            predictions={predictions.awards}
            onUpdate={(awards) => !isReadOnly && setPredictions(prev => ({ ...prev, awards }))}
          />
        )
      default:
        return null
    }
  }

  if (isLoadingGroups || isLoadingExisting || (hasExistingPrediction && isLoadingFull)) {
    return (
      <div className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto space-y-6">
        <Skeleton className="h-12 w-64 rounded-xl" />
        <Skeleton className="h-8 w-full rounded-xl" />
        <Skeleton className="h-64 w-full rounded-2xl" />
      </div>
    )
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto">
      {/* Encabezado */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4"
      >
        <div>
          <h1 className="text-2xl sm:text-3xl font-display font-bold text-text-primary">
            Arma tu predicción
          </h1>
          <p className="text-text-secondary mt-1">
            Completa cada sección para tener tu predicción lista antes del inicio del torneo.
          </p>
        </div>
        {isReadOnly && (
          <Badge variant="warning" size="lg" icon={Lock} className="self-start sm:self-center">
            Predicción cerrada (Solo lectura)
          </Badge>
        )}
        {hasExistingPrediction && !isReadOnly && (
          <Badge variant="success" size="lg" className="self-start sm:self-center">
            Predicción editable (Guardada)
          </Badge>
        )}
      </motion.div>

      {/* Indicador de pasos */}
      <div className="mb-8">
        <div className="flex items-center gap-2 overflow-x-auto pb-2">
          {wizardSteps.map((step, index) => {
            const isActive = index === currentStep
            const isCompleted = index < currentStep
            const StepIcon = step.icon

            return (
              <button
                key={step.key}
                onClick={() => goToStep(index)}
                className={`
                  flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium font-display
                  whitespace-nowrap transition-all duration-200 shrink-0 cursor-pointer
                  ${isActive
                    ? 'bg-cyan text-navy-dark shadow-cyan'
                    : isCompleted
                      ? 'glass text-success'
                      : 'glass text-text-muted hover:text-text-secondary'
                  }
                `}
              >
                {isCompleted ? (
                  <Check className="w-4 h-4" />
                ) : (
                  <StepIcon className="w-4 h-4" />
                )}
                <span className="hidden sm:inline">{step.label}</span>
                <span className="sm:hidden">{index + 1}</span>
              </button>
            )
          })}
        </div>

        {/* Barra de progreso general */}
        <div className="mt-3 flex items-center gap-3">
          <div className="flex-1 h-1.5 bg-surface-card rounded-full overflow-hidden">
            <motion.div
              className="h-full bg-cyan rounded-full"
              animate={{ width: `${((currentStep + 1) / wizardSteps.length) * 100}%` }}
              transition={{ duration: 0.4 }}
            />
          </div>
          <span className="text-xs text-text-muted font-mono">
            {currentStep + 1}/{wizardSteps.length}
          </span>
        </div>
      </div>

      {/* Contenido del paso actual con animación */}
      <div className="min-h-[60vh] relative overflow-hidden">
        <AnimatePresence mode="wait" custom={direction}>
          <motion.div
            key={currentStep}
            custom={direction}
            variants={pageVariants}
            initial="enter"
            animate="center"
            exit="exit"
          >
            {renderStepContent()}
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Navegación inferior */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="mt-8 flex items-center justify-between gap-4 pt-6 border-t border-border-subtle"
      >
        {/* Botón anterior */}
        <Button
          variant="ghost"
          icon={ChevronLeft}
          onClick={goPrev}
          disabled={currentStep === 0}
        >
          Anterior
        </Button>

        {!isReadOnly ? (
          <div className="flex items-center gap-3">
            {/* Guardar borrador */}
            {!hasExistingPrediction && (
              <Button
                variant="secondary"
                icon={Save}
                onClick={handleSave}
              >
                <span className="hidden sm:inline">Guardar borrador</span>
                <span className="sm:hidden">Guardar</span>
              </Button>
            )}

            {/* Siguiente o Enviar/Actualizar */}
            {currentStep < wizardSteps.length - 1 ? (
              <Button
                iconRight={ChevronRight}
                onClick={goNext}
              >
                Siguiente
              </Button>
            ) : (
              <Button
                variant="success"
                icon={Check}
                onClick={handleSubmit}
              >
                {hasExistingPrediction ? 'Actualizar predicción' : 'Enviar predicción'}
              </Button>
            )}
          </div>
        ) : (
          /* En modo lectura, solo botón Siguiente si no es el último paso */
          currentStep < wizardSteps.length - 1 && (
            <Button
              iconRight={ChevronRight}
              onClick={goNext}
            >
              Siguiente
            </Button>
          )
        )}
      </motion.div>
    </div>
  )
}
